import { Paragraph, ParagraphType, ParagraphDataType, DictType } from "@/model";
import * as ParagraphData from "@/model/paragraph";
import { formatJSON, isValidJSON } from "@/util/stringUtil";
import { CODE_EDITOR_DEFAULT_CODE } from "@/component/input";
import {
    isCarousel,
    isCode,
    isCustom,
    isLLMAnswer,
    isList,
    isMedia,
    isNext,
    isQuickReply,
    isRaw,
    isTextInput,
    isTextOutput,
    filterNextConditionData
} from "./typeGuards";


const toArrayFromTextData = (data: ParagraphData.ParagraphOutputText['data']): ParagraphData.TextItem[] => {
    if (Array.isArray(data)) {
        return data;
    }
    if (typeof data === "string") {
        return [{ text: data }];
    }
    return [data];
};

export const getNewTextItem = (): ParagraphData.TextItem => ({ text: "" });
export const getNewCondition = (): ParagraphData.NextConditionData => ({ condition: "", next: "" });
export const getNewButton = (): ParagraphData.ParagraphButtonData => ({ label: "", button_type: "text" as const });
export const getNewCarousel = (data?: Partial<ParagraphData.CarouselCardItem>): ParagraphData.CarouselCardItem => ({ buttons: [], ...data });

export const removeMediaData = (data: ParagraphData.MediaData & DictType): DictType => {
    const { media_type, media_url, media_width, media_height, file_size, ...rest } = data;
    return rest;
};
const extractMediaData = (item: ParagraphData.CarouselCardItem): ParagraphData.MediaData => {
    return {
        media_type: item.media_type,
        media_url: item.media_url,
        media_width: item.media_width,
        media_height: item.media_height,
        file_size: item.file_size
    };
};

export const convertParagraph = (prev: Paragraph, type: ParagraphType, dataType: ParagraphDataType): Paragraph => {
    if ((prev.type === type && prev.data_type === dataType)) {
        return prev;
    }

    const next: any = {
        ...structuredClone(prev),
        type,
        data_type: dataType,
        data: undefined
    };
    if (prev.data === undefined) {
        return next;
    }

    // Text <-> Carousel LLM : text content
    if (isTextOutput(prev)) {
        const textArray = toArrayFromTextData(prev.data);
        if (textArray.length === 0) {
            textArray.push(getNewTextItem());
        }
        if (isCarousel(next)) {
            return { ...next, data: textArray.map(getNewCarousel) };
        }
        if (isLLMAnswer(next)) {
            return { ...next, data: { text: textArray[0]?.text ?? "" } };
        }
    }
    if (isTextOutput(next)) {
        if (isCarousel(prev)) {
            return { ...next, data: prev.data?.map(item => ({ text: item.text ?? "" })) ?? [getNewTextItem()] };
        }
        if (isLLMAnswer(prev)) {
            return { ...next, data: { text: prev.data.text ?? "" } };
        }
    }

    // List Quick Reply Text_Input Next : next condition content
    if (isList(prev) || isQuickReply(prev) || isTextInput(prev) || isNext(prev)) {
        if (isQuickReply(next) || isList(next)) {
            return { ...next, data: prev.data ?? [] };
        }
        if (isTextInput(next) || isNext(next)) {
            const nextConditions = filterNextConditionData(prev.data ?? []);
            if (!nextConditions.length && isTextInput(next)) {
                return { ...next, data: undefined };
            }
            return { ...next, data: nextConditions };
        }
    }

    // Custom <-> Raw : json content
    if (isCustom(prev) && isRaw(next)) {
        return { ...next, data: formatJSON(prev.data) };
    }
    if (isRaw(prev) && isCustom(next)) {
        if (isValidJSON(prev.data)) {
            return { ...next, data: JSON.parse(prev.data) };
        } else {
            return next;
        }
    }

    // Media <-> Carousel : media contnet
    if (isMedia(prev)) {
        if (isCarousel(next)) {
            return { ...next, data: [getNewCarousel({ ...prev.data })] };
        }
    }
    if (isCarousel(prev) && isMedia(next)) {
        const mediaData = extractMediaData(prev.data[0]);
        if (mediaData.media_type === "image_square") {
            mediaData.media_type = "image";
        }
        return { ...next, data: mediaData };
    }

    // -> Code : python code
    if (isCode(next)) {
        return { ...next, data: { code: CODE_EDITOR_DEFAULT_CODE } };
    }

    return next;
};

